import { useEffect, useRef, useState } from 'react';
import { player } from '@/lib/player.ts';
import { Focusable, navProps } from '@/ui/Focusable.tsx';
import { pushKeyLayer, type NavKey } from '@/lib/keys.ts';

/**
 * Skip, scrub, and a bar that says where you are.
 *
 * NONE OF THIS EXISTED ON ANY PLATFORM. A film could be started and paused
 * and nothing else — miss a line and your only option was to begin it again.
 * That is the single largest gap against the app this replaces, and it is not
 * a preference: a timeline you cannot move along is not really a player.
 *
 * Only for things with a timeline. A live channel has no length and no end to
 * scrub towards, and offering a bar that cannot move would be worse than
 * offering nothing.
 */

/** Ten seconds, because that is what every player settled on. */
const STEP_S = 10;

function clock(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0:00';
  const total = Math.floor(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${m}:${String(s).padStart(2, '0')}`;
}

export function Transport() {
  const [{ positionMs, durationMs }, setAt] = useState(() => player.progress());
  /*
   * POLLED, not driven by timeupdate events.
   *
   * The native path has no media element to fire them — the picture comes
   * from ExoPlayer on a surface behind the WebView — so an event-driven bar
   * would sit at zero on the televisions, which are most of the audience.
   * Twice a second is smooth enough for a progress bar and cheap enough for a
   * Fire TV Stick.
   */
  useEffect(() => {
    const t = setInterval(() => setAt(player.progress()), 500);
    return () => clearInterval(t);
  }, []);

  // While a drag is in progress the bar must follow the FINGER, not the film.
  const [scrubMs, setScrubMs] = useState<number | null>(null);
  const bar = useRef<HTMLDivElement>(null);
  /*
   * A REF, not the state above, to decide whether a drag is happening.
   *
   * `scrubMs` is read by the move handler out of the render closure, so it is
   * only correct once React has re-rendered — and the first pointermove can
   * arrive before that. A ref is set synchronously in pointerdown and is
   * therefore true by the time the very next event fires. The state is for
   * DRAWING; this is for deciding.
   */
  const dragging = useRef(false);

  /*
   * SCRUBBING WITH A REMOTE, which a pointer-only bar cannot do.
   *
   * A Fire TV has no pointer at all, so the draggable bar was furniture there
   * — ten-second steps and nothing else, which is no way to find a scene
   * forty minutes in. This is the pattern every television player uses: land
   * on the bar, move a PENDING position with left and right, press OK to go
   * there, BACK to change your mind.
   *
   * Pending, not immediate: seeking on every press would re-buffer the stream
   * a dozen times while somebody hunts for a scene, and on a transcode that
   * means Plex re-encoding from each spot in turn.
   */
  const [pendingMs, setPendingMs] = useState<number | null>(null);
  const pendingRef = useRef<number | null>(null);
  pendingRef.current = pendingMs;
  const lastPress = useRef(0);
  const stepSize = useRef(STEP_S);
  const lengthRef = useRef(0);
  const release = useRef<(() => void) | null>(null);
  // Belt and braces: a bar that unmounts while focused (the film ends, the
  // chrome hides) must not leave its layer on the stack swallowing arrows.
  useEffect(() => () => release.current?.(), []);

  /*
   * Hold the last real duration.
   *
   * A transcoded HLS stream reports Infinity until enough of the playlist has
   * been read, and `progress()` turns that into 0 — so the bar would appear,
   * vanish, and reappear, and a drag in progress would be torn down with it.
   * Once a film has told us how long it is, that does not change.
   */
  const lastDuration = useRef(0);
  if (durationMs > 0) lastDuration.current = durationMs;
  const length = durationMs > 0 ? durationMs : lastDuration.current;

  lengthRef.current = length;

  if (!player.seekable() || length <= 0) return null;

  const shown = scrubMs ?? pendingMs ?? positionMs;
  const pct = Math.min(100, Math.max(0, (shown / length) * 100));

  const step = (dir: -1 | 1) => {
    /*
     * ACCELERATION, because a two-hour film at ten seconds a press is seven
     * hundred presses. Held or rapidly repeated, the step grows; pause for a
     * moment and it resets, so a single deliberate press is still ten seconds
     * and still predictable.
     */
    const now = Date.now();
    const rapid = now - lastPress.current < 300;
    lastPress.current = now;
    stepSize.current = rapid ? Math.min(stepSize.current * 1.6, 300) : STEP_S;
    const from = pendingRef.current ?? player.progress().positionMs;
    const to = Math.min(Math.max(0, from + dir * stepSize.current * 1000), lengthRef.current);
    setPendingMs(to);
  };

  const msAt = (clientX: number): number => {
    const box = bar.current?.getBoundingClientRect();
    if (!box || box.width === 0) return shown;
    return ((clientX - box.left) / box.width) * length;
  };

  return (
    <div className="transport">
      <Focusable
        id="mp-back10"
        className="btn ghost transport-skip"
        onSelect={() => player.seekBy(-STEP_S)}
      >
        <span aria-hidden="true">⟲</span> {STEP_S}
      </Focusable>

      {/*
        NO PLAY/PAUSE HERE. The now-playing bar already has one (`mp-toggle`)
        and a second would be both a duplicate control and — worse — a
        duplicate `data-nav` id, which is what focus memory and every
        `[data-nav="..."]` lookup key on. Two elements answering to one name
        is a cursor that lands in the wrong place at random.
      */}
      <Focusable
        id="mp-fwd10"
        className="btn ghost transport-skip"
        onSelect={() => player.seekBy(STEP_S)}
      >
        <span aria-hidden="true">⟳</span> {STEP_S}
      </Focusable>

      <span className="transport-time">{clock(shown)}</span>

      {/*
        POINTER EVENTS, not mouse events: one set of handlers covers a mouse,
        a finger and a stylus, and `setPointerCapture` keeps the drag alive
        when the pointer leaves the bar — which it always does, because people
        drag diagonally.
      */}
      <div
        ref={bar}
        className={`transport-bar${pendingMs !== null ? ' scrubbing' : ''}`}
        role="slider"
        aria-label="Position"
        {...navProps('mp-scrub')}
        /*
         * The layer is pushed on FOCUS and popped on BLUR, so it exists only
         * while the cursor is actually on the bar. usePlayerKeys sits below
         * it and keeps left/right meaning "skip ten seconds" everywhere else,
         * which is what they should mean when you are just watching.
         */
        onFocus={() => {
          /*
           * LANDING ON THE BAR DOES NOTHING. It did start a scrub, and that
           * made the bar a room with no door: the layer below (usePlayerKeys)
           * already has the documented way out — when the cursor is in the
           * chrome, UP or BACK blurs and leaves — and this layer sat on top
           * of it swallowing BACK to cancel a scrub that had been started
           * merely by arriving. Every other button in the bar could be left;
           * this one could not, which on a remote is being stuck.
           *
           * So: keys are OURS ONLY WHILE ACTUALLY SCRUBBING. Idle on the bar,
           * everything falls through and behaves exactly like every other
           * control in the chrome.
           */
          release.current = pushKeyLayer((key: NavKey) => {
            /*
             * ARMED OR NOT — and this is the whole of it.
             *
             * Left and right used to scrub the moment the bar had focus,
             * which quietly took the only keys that move between the controls
             * either side of it. The skip buttons and Back sit to the left and
             * right of this bar; claiming those keys made them unreachable, so
             * the bar was still a trap even after BACK was fixed. Up and down
             * worked, which is exactly why Stop and Pause could still be
             * reached and nothing else could.
             *
             * So the bar is an ordinary control until you ARM it with OK.
             * Idle, every key falls through and it behaves like every other
             * button in the row. Armed, it owns left and right because that
             * is what it is for, and OK or BACK gives them back.
             *
             * This is what a television player does, and the reason is this
             * exact conflict: a slider wants the arrow keys and so does the
             * cursor, so something has to say which mode you are in.
             */
            const armed = pendingRef.current !== null;

            if (armed && key === 'left')  { step(-1); return true; }
            if (armed && key === 'right') { step(1);  return true; }

            if (key === 'select') {
              const to = pendingRef.current;
              if (to === null) {
                setPendingMs(player.progress().positionMs);
                return true;
              }
              setPendingMs(null);
              stepSize.current = STEP_S;
              player.seekTo(to);
              setAt((a) => ({ ...a, positionMs: to }));
              return true;
            }

            /*
             * BACK cancels a scrub IN PROGRESS and nothing else. With nothing
             * pending it is not ours, and falls through to the layer that
             * knows how to leave the chrome. Two presses to get out from
             * mid-scrub — abandon the move, then leave — which is the right
             * number: the first undoes something, the second exits.
             */
            if (key === 'back' && pendingRef.current !== null) {
              setPendingMs(null);
              stepSize.current = STEP_S;
              return true;
            }

            return false;   // up, down, and back-when-idle: not ours
          });
        }}
        onBlur={() => {
          release.current?.();
          release.current = null;
          setPendingMs(null);
          stepSize.current = STEP_S;
        }}
        aria-valuemin={0}
        aria-valuemax={Math.round(length / 1000)}
        aria-valuenow={Math.round(shown / 1000)}
        onPointerDown={(e) => {
          /*
           * STATE FIRST, CAPTURE SECOND — and the capture in a try.
           *
           * `setPointerCapture` throws NotFoundError if the browser no longer
           * considers that pointer active, which happens more than it should.
           * It was the FIRST statement here, so when it threw, the rest of the
           * handler never ran: the drag never started, and the bar sat there
           * refusing to move while a plain click still worked. A control that
           * is visible, obviously draggable, and does nothing when dragged.
           *
           * Capture is an optimisation — it keeps the drag alive when the
           * pointer leaves the bar, which it always does. Losing it degrades
           * the drag; losing the state loses the feature.
           */
          dragging.current = true;
          setScrubMs(msAt(e.clientX));
          try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* drag still works */ }
        }}
        onPointerMove={(e) => { if (dragging.current) setScrubMs(msAt(e.clientX)); }}
        onPointerUp={(e) => {
          const to = dragging.current ? msAt(e.clientX) : shown;
          dragging.current = false;
          setScrubMs(null);
          player.seekTo(to);
          // Show the target immediately rather than waiting for the next poll
          // to catch up, which reads as the bar snapping back.
          setAt((a) => ({ ...a, positionMs: to }));
        }}
        onPointerCancel={() => { dragging.current = false; setScrubMs(null); }}
      >
        <div className="transport-fill" style={{ width: `${pct}%` }} />
        <div className="transport-knob" style={{ left: `${pct}%` }} />
        {/* Armed is a MODE, and a mode you cannot see is a mode you get stuck
            in. This is the only thing on screen that says the arrow keys have
            changed meaning. */}
        {pendingMs !== null && <span className="transport-hint">OK to jump · Back to cancel</span>}
      </div>

      <span className="transport-time transport-total">{clock(length)}</span>
    </div>
  );
}
